import type {
  AcceptInvitationResponse,
  BatchInviteRequest,
  BatchInviteResponse,
  BuySeatsRequest,
  BuySeatsResponse,
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  ReduceSeatsRequest,
  ResubscribeWorkspaceRequest,
  ScheduledChangeType,
  UpdateWorkspaceDetailsRequest,
  Workspace,
  WorkspaceAutoReloadSettings,
  WorkspaceAutoReloadSettingsRequest,
  WorkspaceCreditsSnapshot,
  WorkspaceInvitation,
  WorkspaceInvoice,
  WorkspaceMember,
  WorkspaceRole,
  WorkspaceScheduledChange,
  WorkspaceSummary,
} from "@/api/types"
import type { CreditPackItem } from "@/components/credits/CreditsPackCards/CreditsPackCards"
import type { BillingPlanId } from "@/consts/billing"
import { logger } from "@/lib/logger"
import type { PlanBillingCycle } from "@/pages/LandingPage/sections/Plans/plansData"

import type {
  AutoReloadSettingsRaw,
  BillingStatus,
  InvoiceDescriptionParams,
} from "./billingApi"
import {
  normalizeAutoReloadSettings,
  parseRecordingAccessState,
} from "./billingApi"
import { getErrorResponse } from "./errorUtils"
import { httpClient } from "./httpClient"
import { formatAvatarUrl, toUnixMilliseconds } from "./utils"

interface WorkspaceSummaryRaw {
  id: string
  name: string
  avatar: string
  role: WorkspaceRole
  plan: BillingPlanId
  seatsOccupied: number
  seatsTotal: number
}

interface WorkspaceCreditsSnapshotRaw {
  total: number
  remaining: number
  extraCredits: number
  breakdown: {
    askAi: number
    summary: number
    transcription: number
  }
  used: number
}

interface WorkspaceScheduledChangeRaw {
  type: ScheduledChangeType
  targetSeats: number
  effectiveAt: number
  currentSeats: number
}

interface WorkspaceInvoiceRaw {
  id: string
  number?: string | null
  description: string
  descriptionKey?: string | null
  descriptionParams?: InvoiceDescriptionParams | null
  amountCents: number
  currency: string
  status: string
  hostedUrl?: string | null
  pdfUrl?: string | null
  date: number
}

interface WorkspaceRaw {
  id: string
  name: string
  avatar: string
  role: WorkspaceRole
  plan: BillingPlanId
  interval: PlanBillingCycle
  status: BillingStatus
  currentPeriodEnd?: number | null
  pendingInterval?: "" | PlanBillingCycle
  pendingEffectiveAt?: number | string | null
  cancelAtPeriodEnd: boolean
  seats: {
    total: number
    occupied: number
    available: number
  }
  credits?: WorkspaceCreditsSnapshotRaw | null
  autoReload?: AutoReloadSettingsRaw | null
  scheduledChange?: WorkspaceScheduledChangeRaw | null
  deletionReason?: "owner_requested" | "payment_failed"
  deletionAt?: number | null
  createdAt?: number | null
  creditsRenewAt?: number | null
  cardLastFour?: string
  cardBrand?: string
  cardExpMonth?: number
  cardExpYear?: number
  recordingAccessState?: string
  recordingGraceEndsAt?: number | string | null
}

interface WorkspaceMemberRaw {
  userId?: string | null
  name?: string | null
  username?: string | null
  avatar?: string | null
  email?: string | null
  role: WorkspaceRole
  status: "active" | "pending" | "declined"
  displayIdentifier: string
  joinedAt?: number | null
  invitationId?: string | null
  expiresAt?: number | null
}

interface WorkspaceInvitationRaw {
  id: string
  workspaceId: string
  workspaceName: string
  workspaceAvatar?: string | null
  invitedByName: string
  invitedByAvatar?: string | null
  inviteeIdentifier: string
  role: WorkspaceRole
  expiresAt: number
  createdAt: number
}

export function normalizeWorkspaceInvitation(
  raw: WorkspaceInvitationRaw,
): WorkspaceInvitation {
  return {
    id: raw.id,
    workspaceId: raw.workspaceId,
    workspaceName: raw.workspaceName,
    workspaceAvatar: formatAvatarUrl(raw.workspaceAvatar ?? ""),
    invitedByName: raw.invitedByName,
    invitedByAvatar: raw.invitedByAvatar
      ? formatAvatarUrl(raw.invitedByAvatar)
      : undefined,
    inviteeIdentifier: raw.inviteeIdentifier,
    role: raw.role,
    expiresAt: toUnixMilliseconds(raw.expiresAt) ?? 0,
    createdAt: toUnixMilliseconds(raw.createdAt) ?? 0,
  }
}

export function normalizeWorkspaceMember(
  raw: WorkspaceMemberRaw,
): WorkspaceMember {
  return {
    ...raw,
    userId: raw.userId ?? undefined,
    name: raw.name ?? undefined,
    username: raw.username ?? undefined,
    avatar: raw.avatar ? formatAvatarUrl(raw.avatar) : undefined,
    email: raw.email ?? undefined,
    joinedAt: toUnixMilliseconds(raw.joinedAt),
    invitationId: raw.invitationId ?? undefined,
    expiresAt: toUnixMilliseconds(raw.expiresAt),
  }
}

export function normalizeWorkspaceCredits(
  raw: WorkspaceCreditsSnapshotRaw,
): WorkspaceCreditsSnapshot {
  return {
    extraCredits: raw.extraCredits,
    breakdown: {
      askAi: raw.breakdown.askAi,
      summary: raw.breakdown.summary,
      transcription: raw.breakdown.transcription,
    },
    tariffCreditsTotal: raw.total,
    tariffCreditsLeft: raw.remaining - raw.extraCredits,
  }
}

export function normalizeScheduledChange(
  raw: WorkspaceScheduledChangeRaw,
): WorkspaceScheduledChange {
  return {
    ...raw,
    effectiveAt: toUnixMilliseconds(raw.effectiveAt) ?? 0,
  }
}

export function normalizeWorkspaceInvoice(
  raw: WorkspaceInvoiceRaw,
): WorkspaceInvoice {
  return {
    ...raw,
    number: raw.number ?? undefined,
    descriptionKey: raw.descriptionKey ?? undefined,
    descriptionParams: raw.descriptionParams ?? undefined,
    hostedUrl: raw.hostedUrl ?? undefined,
    pdfUrl: raw.pdfUrl ?? undefined,
    date: toUnixMilliseconds(raw.date) ?? 0,
  }
}

export function normalizeWorkspaceSummary(
  raw: WorkspaceSummaryRaw,
): WorkspaceSummary {
  return {
    ...raw,
    avatar: formatAvatarUrl(raw.avatar),
  }
}

export function normalizeWorkspace(raw: WorkspaceRaw): Workspace {
  return {
    ...raw,
    avatar: formatAvatarUrl(raw.avatar),
    currentPeriodEnd:
      raw.currentPeriodEnd != null
        ? (toUnixMilliseconds(raw.currentPeriodEnd) ?? null)
        : null,
    pendingInterval: raw.pendingInterval ?? "",
    pendingEffectiveAt: toUnixMilliseconds(raw.pendingEffectiveAt) ?? null,
    creditsRenewAt:
      raw.creditsRenewAt != null
        ? (toUnixMilliseconds(raw.creditsRenewAt) ?? null)
        : null,
    cancelAtPeriodEnd: raw.cancelAtPeriodEnd ?? false,
    credits: raw.credits
      ? normalizeWorkspaceCredits(raw.credits)
      : {
          tariffCreditsTotal: 0,
          tariffCreditsLeft: 0,
          breakdown: { askAi: 0, summary: 0, transcription: 0 },
          extraCredits: 0,
        },
    autoReload: raw.autoReload
      ? normalizeAutoReloadSettings(raw.autoReload)
      : undefined,
    scheduledChange: raw.scheduledChange
      ? normalizeScheduledChange(raw.scheduledChange)
      : undefined,
    deletionAt:
      raw.deletionAt != null
        ? (toUnixMilliseconds(raw.deletionAt) ?? undefined)
        : undefined,
    cardBrand: raw.cardBrand ? raw.cardBrand.toUpperCase() : undefined,
    createdAt: toUnixMilliseconds(raw.createdAt) ?? 0,
    recordingAccessState: parseRecordingAccessState(raw.recordingAccessState),
    recordingGraceEndsAt:
      raw.recordingGraceEndsAt != null
        ? (toUnixMilliseconds(raw.recordingGraceEndsAt) ?? undefined)
        : undefined,
  }
}

export const workspaceApi = {
  async getMyWorkspaces(): Promise<WorkspaceSummary[]> {
    logger.log("workspace api: getMyWorkspaces")
    const response = await httpClient.get<WorkspaceSummaryRaw[]>("/workspace")
    return response.data.map(normalizeWorkspaceSummary)
  },

  async getWorkspace(workspaceId: string): Promise<Workspace> {
    logger.log("workspace api: getWorkspace", { workspaceId })
    const response = await httpClient.get<WorkspaceRaw>(
      `/workspace/${workspaceId}`,
    )
    return normalizeWorkspace(response.data)
  },

  async createWorkspace(
    req: CreateWorkspaceRequest,
  ): Promise<CreateWorkspaceResponse> {
    logger.log("workspace api: createWorkspace", { req })
    const response = await httpClient.post<CreateWorkspaceResponse>(
      "/workspace",
      req,
    )
    return response.data
  },

  async updateWorkspaceDetails(
    req: UpdateWorkspaceDetailsRequest,
  ): Promise<Workspace> {
    const { workspaceId, ...body } = req
    logger.log("workspace api: updateWorkspaceDetails", { workspaceId, body })
    const response = await httpClient.patch<WorkspaceRaw>(
      `/workspace/${workspaceId}`,
      body,
    )
    return normalizeWorkspace(response.data)
  },

  async uploadWorkspaceAvatar(
    workspaceId: string,
    image: File | Blob,
  ): Promise<{ file: string }> {
    logger.log("workspace api: uploadWorkspaceAvatar", { workspaceId })
    const formData = new FormData()
    formData.append("image", image)
    const response = await httpClient.post<{ file: string }>(
      `/workspace/${workspaceId}/upload-image`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    )
    return { file: formatAvatarUrl(response.data.file) }
  },

  async deleteWorkspace(workspaceId: string): Promise<void> {
    logger.log("workspace api: deleteWorkspace", { workspaceId })
    await httpClient.delete(`/workspace/${workspaceId}`)
  },

  async reactivateWorkspacePlan(workspaceId: string): Promise<Workspace> {
    logger.log("workspace api: reactivateWorkspacePlan", { workspaceId })
    const response = await httpClient.post<WorkspaceRaw>(
      `/workspace/${workspaceId}/reactivate`,
    )
    return normalizeWorkspace(response.data)
  },

  async resubscribeWorkspace(
    req: ResubscribeWorkspaceRequest,
  ): Promise<CreateWorkspaceResponse> {
    logger.log("workspace api: resubscribeWorkspace", { req })
    const { workspaceId, ...body } = req
    const response = await httpClient.post<CreateWorkspaceResponse>(
      `/workspace/${workspaceId}/resubscribe`,
      body,
    )
    return response.data
  },

  async leaveWorkspace(workspaceId: string): Promise<void> {
    logger.log("workspace api: leaveWorkspace", { workspaceId })
    await httpClient.post(`/workspace/${workspaceId}/leave`)
  },

  async getMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    logger.log("workspace api: getMembers", { workspaceId })
    const response = await httpClient.get<WorkspaceMemberRaw[]>(
      `/workspace/${workspaceId}/members`,
    )
    return response.data.map(normalizeWorkspaceMember)
  },

  async removeMember(workspaceId: string, userId: string): Promise<void> {
    logger.log("workspace api: removeMember", { workspaceId, userId })
    await httpClient.delete(`/workspace/${workspaceId}/members/${userId}`)
  },

  async changeMemberRole(
    workspaceId: string,
    userId: string,
    role: WorkspaceRole,
  ): Promise<void> {
    logger.log("workspace api: changeMemberRole", { workspaceId, userId, role })
    await httpClient.patch(`/workspace/${workspaceId}/members/${userId}/role`, {
      role,
    })
  },

  async inviteByEmailBatch(
    req: BatchInviteRequest,
  ): Promise<BatchInviteResponse> {
    logger.log("workspace api: inviteByEmailBatch", { req })
    try {
      const response = await httpClient.post<BatchInviteResponse>(
        `/workspace/${req.workspaceId}/invite-batch`,
        { items: req.items },
      )
      return response.data
    } catch (error) {
      const errResp = getErrorResponse(error)
      if (
        errResp?.data &&
        typeof errResp.data === "object" &&
        "blockedBy" in errResp.data
      ) {
        return errResp.data as BatchInviteResponse
      }
      throw error
    }
  },

  async revokeInvitation(
    workspaceId: string,
    invitationId: string,
  ): Promise<void> {
    logger.log("workspace api: revokeInvitation", { workspaceId, invitationId })
    await httpClient.post(
      `/workspace/${workspaceId}/invitations/${invitationId}/revoke`,
    )
  },

  async getInvitationById(invitationId: string): Promise<WorkspaceInvitation> {
    logger.log("workspace api: getInvitationById", { invitationId })
    const response = await httpClient.get<WorkspaceInvitationRaw>(
      `/workspace/invitations/${invitationId}`,
    )
    return normalizeWorkspaceInvitation(response.data)
  },

  async declineInvitation(invitationId: string): Promise<void> {
    logger.log("workspace api: declineInvitation", { invitationId })
    await httpClient.post(`/workspace/invitations/${invitationId}/decline`)
  },

  async acceptInvitation(
    invitationId: string,
  ): Promise<AcceptInvitationResponse> {
    logger.log("workspace api: acceptInvitation", { invitationId })
    const response = await httpClient.post<AcceptInvitationResponse>(
      `/workspace/invitations/${invitationId}/accept`,
    )
    return response.data
  },

  async previewSeats(
    workspaceId: string,
    count: number,
  ): Promise<{
    count: number
    newSeats: number
    amountCents: number
    currency: string
  }> {
    logger.log("workspace api: previewSeats", { workspaceId, count })
    const response = await httpClient.get<{
      count: number
      newSeats: number
      amountCents: number
      currency: string
    }>(`/workspace/${workspaceId}/seats/preview`, { params: { count } })
    return response.data
  },

  async buySeats(req: BuySeatsRequest): Promise<BuySeatsResponse> {
    logger.log("workspace api: buySeats", { req })
    const response = await httpClient.post<WorkspaceRaw>(
      `/workspace/${req.workspaceId}/seats/buy`,
      { count: req.count },
    )
    return normalizeWorkspace(response.data)
  },

  async scheduleReduceSeats(
    req: ReduceSeatsRequest,
  ): Promise<WorkspaceScheduledChange> {
    logger.log("workspace api: scheduleReduceSeats", { req })
    const response = await httpClient.post<WorkspaceScheduledChangeRaw>(
      `/workspace/${req.workspaceId}/seats/reduce`,
      { targetSeats: req.targetSeats },
    )
    return normalizeScheduledChange(response.data)
  },

  async cancelScheduledChange(workspaceId: string): Promise<void> {
    logger.log("workspace api: cancelScheduledChange", { workspaceId })
    await httpClient.delete(`/workspace/${workspaceId}/scheduled-change`)
  },

  async openWorkspaceBillingPortal(
    workspaceId: string,
    returnPath: string,
  ): Promise<string> {
    logger.log("workspace api: openWorkspaceBillingPortal", {
      workspaceId,
      returnPath,
    })
    const response = await httpClient.post<{ url: string }>(
      `/workspace/${workspaceId}/billing/portal`,
      { returnPath },
    )
    return response.data.url
  },

  async checkWorkspaceCredits(
    workspaceId: string,
  ): Promise<WorkspaceCreditsSnapshot> {
    logger.log("workspace api: checkWorkspaceCredits", { workspaceId })
    const response = await httpClient.get<WorkspaceCreditsSnapshotRaw>(
      `/workspace/${workspaceId}/credits`,
    )
    return normalizeWorkspaceCredits(response.data)
  },

  async getCreditsPackPricing(workspaceId: string): Promise<CreditPackItem[]> {
    logger.log("workspace api: getCreditsPackPricing", { workspaceId })
    const response = await httpClient.get<CreditPackItem[]>(
      `/workspace/${workspaceId}/credit-packs`,
    )
    return response.data
  },

  async buyCreditsPack(
    workspaceId: string,
    packSize: number,
    successPath: string,
    cancelPath: string,
  ): Promise<{ url: string }> {
    logger.log("workspace api: buyCreditsPack", { workspaceId, packSize })
    const response = await httpClient.post<{ url: string }>(
      `/workspace/${workspaceId}/credit-packs/buy`,
      { packSize, successPath, cancelPath },
    )
    return response.data
  },

  async getAutoReloadSettings(
    workspaceId: string,
  ): Promise<WorkspaceAutoReloadSettings | null> {
    logger.log("workspace api: getAutoReloadSettings", { workspaceId })
    const response = await httpClient.get<AutoReloadSettingsRaw | null>(
      `/workspace/${workspaceId}/auto-reload`,
    )
    return response.data ? normalizeAutoReloadSettings(response.data) : null
  },

  async saveAutoReloadSettings(
    workspaceId: string,
    settings: WorkspaceAutoReloadSettingsRequest,
  ): Promise<WorkspaceAutoReloadSettings> {
    logger.log("workspace api: saveAutoReloadSettings", { workspaceId })

    const response = await httpClient.put<AutoReloadSettingsRaw>(
      `/workspace/${workspaceId}/auto-reload`,
      settings,
    )
    return normalizeAutoReloadSettings(response.data)
  },

  async deleteAutoReloadSettings(workspaceId: string): Promise<void> {
    logger.log("workspace api: deleteAutoReloadSettings", { workspaceId })
    await httpClient.delete(`/workspace/${workspaceId}/auto-reload`)
  },

  async retryAutoReloadPayment(
    workspaceId: string,
  ): Promise<WorkspaceAutoReloadSettings> {
    logger.log("workspace api: retryAutoReloadPayment", { workspaceId })
    const response = await httpClient.post<AutoReloadSettingsRaw>(
      `/workspace/${workspaceId}/auto-reload/retry`,
    )
    return normalizeAutoReloadSettings(response.data)
  },

  async getWorkspaceInvoices(workspaceId: string): Promise<WorkspaceInvoice[]> {
    logger.log("workspace api: getWorkspaceInvoices", { workspaceId })
    const response = await httpClient.get<WorkspaceInvoiceRaw[]>(
      `/workspace/${workspaceId}/invoices`,
    )
    return response.data.map(normalizeWorkspaceInvoice)
  },

  async cancelWorkspacePlan(workspaceId: string): Promise<Workspace> {
    logger.log("workspace api: cancelWorkspacePlan", { workspaceId })
    const response = await httpClient.post<WorkspaceRaw>(
      `/workspace/${workspaceId}/cancel`,
    )
    return normalizeWorkspace(response.data)
  },

  async changeWorkspacePlanInterval(
    workspaceId: string,
    interval: PlanBillingCycle,
  ): Promise<Workspace> {
    logger.log("workspace api: changeWorkspacePlanInterval", {
      workspaceId,
      interval,
    })
    const response = await httpClient.post<WorkspaceRaw>(
      `/workspace/${workspaceId}/change-interval`,
      { interval },
    )
    return normalizeWorkspace(response.data)
  },
}
